import throttle from "lodash/throttle"

interface HeaderElements {
  header: HTMLElement | null
  headerContainerHeight: number | undefined
  headerHeight: number | undefined
  headerOverride: boolean
  navButtons: NodeListOf<HTMLAnchorElement>
  elementsToHide: Array<{ element: HTMLElement; height: number }> | []
}

export default function setupHeaderScrollingAnimation(): void {
  const { header, headerContainerHeight, headerHeight, headerOverride, navButtons, elementsToHide } =
    getHeaderElements()

  if (!header || !headerContainerHeight) {
    return
  }

  const onScroll = throttle(() => {
    requestAnimationFrame(() => {
      checkScrollHeight(header, headerContainerHeight, headerHeight, headerOverride, navButtons, elementsToHide)
    })
  }, 30)

  window.addEventListener("scroll", onScroll, { passive: true })
  window.addEventListener("pagehide", () => {
    window.removeEventListener("scroll", onScroll, { passive: true })
  })
}

function getHeaderElements(): HeaderElements {
  const header: HTMLElement | null = document.querySelector(".ifttt-next.unified-header")
  const headerHeight: number | undefined = header?.getBoundingClientRect().height
  const headerOverride: boolean = overrideHeaderStyling()
  const headerContainerHeight = validHeaderHeight()
  const navButtons: NodeListOf<HTMLAnchorElement> = document.querySelectorAll("a.button-on-color")
  const elementsToHide: Array<{ element: HTMLElement; height: number }> | [] = elementsToHideOnScroll()

  return {
    header,
    headerContainerHeight,
    headerHeight,
    headerOverride,
    navButtons,
    elementsToHide,
  }
}

const checkScrollHeight = (
  header: HTMLElement,
  headerContainerHeight: number,
  headerHeight: number | undefined,
  headerOverride: boolean,
  navButtons: NodeListOf<HTMLAnchorElement>,
  elementsToHide: Array<{ element: HTMLElement; height: number }> | []
): void => {
  const userScrollHeight = document.body.getBoundingClientRect().top
  const backgroundColor = document.body.style.backgroundColor
  const totalHeaderHeight = userScrollHeight + headerContainerHeight - (headerHeight ?? 0)

  if (totalHeaderHeight <= 0) {
    header.classList.add("opacity-100")
    header.classList.remove("on-color", "on-image", "dark")
    header.style.backgroundColor = "rgb(255,255,255)"

    navButtons?.forEach(button => {
      button.classList.remove("button-on-color")
    })
  } else {
    if (!headerOverride) {
      header.classList.add("on-color", "on-image")
    }
    header.classList.remove("opacity-100")
    header.style.backgroundColor = backgroundColor

    navButtons?.forEach(button => {
      button.classList.add("button-on-color")
    })
  }

  hideElementsOnScroll(elementsToHide, headerHeight, userScrollHeight)
}

// used when you want to maintain the black color font on the header
function overrideHeaderStyling(): boolean {
  const headers = [
    ".hero--header", // /partners header
    ".header-container", // /solutions header
  ]

  let result: HTMLElement | undefined
  headers.forEach(className => {
    const validHeader: HTMLElement | null = document.querySelector(className)

    if (validHeader) result = validHeader
  })

  return !!result
}

function elementsToHideOnScroll(): Array<{ element: HTMLElement; height: number }> | [] {
  const classes = [
    ".learn-more-icon",
    ".back-button-fixed", // stories, /archive, etc. must hide back button due to see-thruough header on these pages
  ]
  const validElements: Array<{ element: HTMLElement; height: number }> = []

  classes.forEach(className => {
    const element: HTMLElement | null = document.querySelector(className)
    if (element) {
      validElements.push({
        element,
        height: element.getBoundingClientRect().top,
      })
    }
  })

  return validElements
}

function hideElementsOnScroll(
  elements: Array<{ element: HTMLElement; height: number }> | [],
  headerHeight: number | undefined,
  userScrollHeight: number
): void {
  if (elements.length === 0) return

  elements.forEach(obj => {
    if (obj.element && obj.height && headerHeight) {
      if (headerHeight - userScrollHeight > obj.height) {
        obj.element.style.display = "none"
      } else {
        obj.element.style.display = "inline-flex"
      }
    }
  })
}

function validHeaderHeight(): number | undefined {
  const headers = [
    ".ifttt-next.unified-header:not(.with-service-selector)",
    ".story-header-container",
    ".connection-header",
    ".header-container", // /solutions pages
    ".connect-header-container", // e.g. /connect/discord/twitter
    ".service-header",
    ".hero--home.bg--black", // logged out home screen hero image
    ".hero--header", // /partners hero image
  ]

  let height: number | undefined
  headers.forEach(className => {
    const elementHeight = document.querySelector(className)?.getBoundingClientRect().height

    if (elementHeight) {
      height = elementHeight
    }
  })

  return height
}
